import { currentTenantPrefix } from '../tenant';
import { studentSiteUrl, teacherSiteUrl } from '../front-door/urls';

export type AppRole = 'student' | 'teacher';

/**
 * A person signed in successfully, at the other role's app.
 *
 * Thrown by an app's AuthContext *after* the server has authenticated the
 * person, never before — so it tells nothing about an account to someone who
 * does not hold it. The session is discarded, not carried across; the link
 * opens the right app's sign-in with the identifier filled in, and they sign in
 * once more there.
 *
 * Shaped like ApiError (`detailWord` + `context.signInUrl`) so LoginPage's
 * existing error plumbing carries it. See front-door-target.md, "Wrong door".
 */
export class WrongAppError extends Error {
  readonly detailWord = 'WRONG_APP';
  readonly context: { signInUrl: string; signInLabel: string };

  constructor(accountRole: AppRole, identifier?: string) {
    super(accountRole === 'student' ? 'This is a student account.' : 'This is a teacher account.');
    this.name = 'WrongAppError';
    const origin = accountRole === 'student' ? studentSiteUrl() : teacherSiteUrl();
    // A school's URL carries its prefix; the other app answers on the same one.
    const base = currentTenantPrefix().basename.replace(/\/$/, '');
    const query = identifier ? `?login=${encodeURIComponent(identifier)}` : '';
    this.context = {
      signInUrl: `${origin}${base}/login${query}`,
      signInLabel: accountRole === 'student' ? 'Continue to the student app' : 'Continue to the teacher app',
    };
  }
}
